import React, { useCallback, useState } from 'react';
import cx from 'classnames';

import { Analytics } from '@trello/atlassian-analytics';
import { EventContainer } from '@atlassiansox/analytics-web-client';

import DragSort from 'app/scripts/views/lib/drag-sort';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';
import {
  objectResolverClient,
  ResolveResponse,
} from '@trello/smart-card/object-resolver-client';
import {
  AkSmartCardWithProvider,
  useSmartLinkAnalytics,
  useAkSmartCardProviderRefresh,
} from 'app/src/components/SmartMedia';
import { ChunkLoadErrorBoundary } from 'app/src/components/ChunkLoadErrorBoundary';

import { EditCardButton, useEditCardButton } from './EditCardButton';
import styles from './LinkCard.less';

const PROVIDER_KEYS_WITH_BADGE = [
  'jira-object-provider',
  'bitbucket-object-provider',
];

interface LinkCardProps {
  url: string;
  openEditor: () => void;
  hideCover?: boolean;
  locale?: string;
  isEditable: boolean;
  hideActions?: boolean;
  analyticsContainers?: EventContainer;
}

export const LinkCard = ({
  url,
  openEditor,
  hideCover,
  isEditable,
  hideActions,
  analyticsContainers,
}: LinkCardProps) => {
  const {
    showEditCardButton,
    hideEditCardButton,
    shouldShowEditCardButton,
  } = useEditCardButton();

  const [hasBadge, setHasBadge] = useState(false);
  const [isSmartCard, setIsSmartCard] = useState(
    objectResolverClient.isCached(url),
  );
  const [resolvedUrl, setResolvedUrl] = useState<ResolveResponse | undefined>();
  const smartLinkAnalytics = useSmartLinkAnalytics(resolvedUrl, {
    source: 'cardView',
    containers: analyticsContainers,
    attributes: {
      component: 'linkCard',
    },
  });
  const akSmartCardProviderKey = useAkSmartCardProviderRefresh(resolvedUrl);

  const handleLinkResolved = useCallback((resolved: ResolveResponse) => {
    setResolvedUrl(resolved);
    setIsSmartCard(resolved.meta.visibility !== 'not_found');
    setHasBadge(
      (resolved.meta.key &&
        PROVIDER_KEYS_WITH_BADGE.includes(resolved.meta.key)) ||
        PROVIDER_KEYS_WITH_BADGE.includes(resolved.meta.definitionId),
    );
  }, []);

  const handleLinkUnresolved = useCallback(() => {
    setResolvedUrl(undefined);
    setIsSmartCard(false);
  }, []);

  const handleSmartCardClick = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'linkCard',
      ...smartLinkAnalytics,
    });
  }, [smartLinkAnalytics]);

  const plainLink = () => (
    <a
      className={styles.link}
      href={url}
      target="_blank"
      onClick={handleSmartCardClick}
    >
      {url}
    </a>
  );

  const getInnerContent = () => {
    return (
      <a
        className={styles.link}
        href={url}
        target="_blank"
        // eslint-disable-next-line react/jsx-no-bind
        onClick={(e) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((DragSort as any).sorting) {
            e.stopPropagation();
            e.preventDefault();
            return;
          }
          e.stopPropagation();
          handleSmartCardClick();
        }}
      >
        <AkSmartCardWithProvider
          akSmartCardProps={{
            appearance: 'block',
            inheritDimensions: true,
            showActions: !hideActions,
          }}
          onLinkResolved={handleLinkResolved}
          onLinkUnresolved={handleLinkUnresolved}
          shouldRender={isSmartCard}
          onSmartCardClick={handleSmartCardClick}
          analyticsContextData={smartLinkAnalytics}
          akSmartCardProviderKey={akSmartCardProviderKey}
          // eslint-disable-next-line react/jsx-no-bind
          plainLink={plainLink}
          url={url}
        />
      </a>
    );
  };

  return (
    <div
      className={cx({
        [styles.smartLinkCard]: isSmartCard,
        [styles.linkCard]: !isSmartCard,
        [styles.hideCover]: hideCover,
        [styles.hasBadge]: hasBadge,
      })}
      onFocus={showEditCardButton}
      onBlur={hideEditCardButton}
      onMouseOver={showEditCardButton}
      onMouseOut={hideEditCardButton}
    >
      <EditCardButton
        onClick={openEditor}
        shouldShow={isEditable && shouldShowEditCardButton}
      />
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-teamplates',
          feature: 'Link Card',
        }}
        sendCrashEvent={false}
        // eslint-disable-next-line react/jsx-no-bind
        errorHandlerComponent={plainLink}
      >
        <ChunkLoadErrorBoundary fallback={plainLink()}>
          {getInnerContent()}
        </ChunkLoadErrorBoundary>
      </ErrorBoundary>
    </div>
  );
};
